import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../components/AuthContext";

const ROLES = ["Broker", "Carrier", "Shipper", "Driver", "Consignee"];

const STATUS_COLORS = {
  CREATED: { bg: "#f3f4f6", color: "#6b7280" },
  POSTED: { bg: "#dbeafe", color: "#1d4ed8" },
  IN_PROGRESS: { bg: "#fef3c7", color: "#92400e" },
  PICKED_UP: { bg: "#d1fae5", color: "#065f46" },
  IN_TRANSIT: { bg: "#e0e7ff", color: "#3730a3" },
  DELIVERED: { bg: "#fce7f3", color: "#9d174d" },
  CLOSED: { bg: "#d1fae5", color: "#065f46" },
};

const STEP_COLORS = {
  COMPLETED: { bg: "#d1fae5", border: "#10b981", icon: "✅", text: "#065f46" },
  CURRENT: { bg: "#dbeafe", border: "#3b82f6", icon: "✍️", text: "#1d4ed8" },
  PENDING: { bg: "#f3f4f6", border: "#d1d5db", icon: "⬜", text: "#9ca3af" },
};

// ============================================================================
// MINI SIGNATURE PAD
// ============================================================================
function MiniSignPad({ onSignature, width = 400, height = 140 }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasContent, setHasContent] = useState(false);

  const getPos = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (cx - rect.left) * 2, y: (cy - rect.top) * 2 };
  }, []);

  const start = useCallback((e) => { e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const p = getPos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); setIsDrawing(true); }, [getPos]);
  const move = useCallback((e) => { if (!isDrawing) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const p = getPos(e); ctx.lineTo(p.x, p.y); ctx.strokeStyle = "#1e3a8a"; ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.stroke(); setHasContent(true); }, [isDrawing, getPos]);
  const stop = useCallback(() => { setIsDrawing(false); if (hasContent) onSignature(canvasRef.current.toDataURL("image/png")); }, [hasContent, onSignature]);
  const clear = () => { canvasRef.current.getContext("2d").clearRect(0, 0, width * 2, height * 2); setHasContent(false); onSignature(null); };

  return (
    <div>
      <div style={{ position: "relative", border: "2px solid #d1d5db", borderRadius: 8, background: "#fafafa" }}>
        <canvas ref={canvasRef} width={width * 2} height={height * 2} style={{ width, height, cursor: "crosshair", display: "block", touchAction: "none" }}
          onMouseDown={start} onMouseMove={move} onMouseUp={stop} onMouseLeave={stop} onTouchStart={start} onTouchMove={move} onTouchEnd={stop} />
        {!hasContent && <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none", color: "#d1d5db" }}>Draw signature here ✍️</div>}
        <div style={{ position: "absolute", bottom: 20, left: 16, right: 16, borderBottom: "1px solid #e5e7eb" }} />
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
        <button onClick={clear} style={{ padding: "3px 10px", background: "#f3f4f6", border: "none", borderRadius: 4, fontSize: 11, cursor: "pointer" }}>Clear</button>
      </div>
    </div>
  );
}

// ============================================================================
// STEP EXECUTION MODAL
// ============================================================================
function StepModal({ step, loadNumber, headers, onClose, onExecuted }) {
  const [signMode, setSignMode] = useState("draw");
  const [signerName, setSignerName] = useState("");
  const [signerRole, setSignerRole] = useState(step.signerRole);
  const [signerTitle, setSignerTitle] = useState("");
  const [signatureData, setSignatureData] = useState(null);
  const [condition, setCondition] = useState("GOOD");
  const [notes, setNotes] = useState("");
  const [executing, setExecuting] = useState(false);
  const typeCanvasRef = useRef(null);

  // Type-to-sign rendering
  useEffect(() => {
    if (signMode !== "type" || !signerName || !typeCanvasRef.current) return;
    const c = typeCanvasRef.current;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.font = "italic 48px Georgia, serif";
    ctx.fillStyle = "#1e3a8a";
    ctx.textBaseline = "middle";
    ctx.fillText(signerName, 20, c.height / 2);
    setSignatureData(c.toDataURL("image/png"));
  }, [signerName, signMode]);

  async function execute() {
    if (!signerName.trim()) return window.alert("Please enter your name");
    if (!signatureData) return window.alert("Please provide your signature");
    setExecuting(true);
    try {
      const res = await fetch(`/api/workflow/loads/${step.loadId}/execute-step`, {
        method: "POST", headers,
        body: JSON.stringify({
          action: step.action,
          signerName, signerRole, signerTitle,
          signatureType: signMode,
          signatureData,
          typedName: signMode === "type" ? signerName : null,
          cargoCondition: condition,
          notes,
        }),
      });
      const data = await res.json();
      if (data.success) { onExecuted(data); onClose(); }
      else window.alert(data.detail || "Failed");
    } catch (e) { window.alert("Error: " + e.message); }
    finally { setExecuting(false); }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: "white", borderRadius: 16, width: 520, maxHeight: "90vh", overflow: "auto" }} onClick={e => e.stopPropagation()}>
        <div style={{ background: "#1e3a8a", color: "white", padding: "16px 24px", borderRadius: "16px 16px 0 0" }}>
          <div style={{ fontSize: 13, opacity: 0.7 }}>Step {step.step} of 4 — Load {loadNumber}</div>
          <div style={{ fontWeight: 900, fontSize: 18 }}>{step.label}</div>
          <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>{step.description}</div>
        </div>

        <div style={{ padding: 24 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ display: "block", marginBottom: 4, fontWeight: 600, fontSize: 13 }}>Full Name *</label>
              <input value={signerName} onChange={e => setSignerName(e.target.value)} placeholder="Full name" style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5e7eb", boxSizing: "border-box" }} />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 4, fontWeight: 600, fontSize: 13 }}>Role</label>
              <select value={signerRole} onChange={e => setSignerRole(e.target.value)} style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5e7eb" }}>
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>

          {/* Cargo condition for pickup/delivery */}
          {(step.action === "PICKUP_SIGN" || step.action === "DELIVERY_SIGN") && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 4, fontWeight: 600, fontSize: 13 }}>Cargo Condition</label>
              <div style={{ display: "flex", gap: 6 }}>
                {[
                  { v: "GOOD", l: "Good", c: "#10b981" },
                  { v: "DAMAGED", l: "Damaged", c: "#ef4444" },
                  { v: "SHORTAGE", l: "Shortage", c: "#f59e0b" },
                ].map(o => (
                  <button key={o.v} onClick={() => setCondition(o.v)} style={{
                    flex: 1, padding: 8, background: condition === o.v ? `${o.c}20` : "#f9fafb",
                    color: condition === o.v ? o.c : "#6b7280",
                    border: condition === o.v ? `2px solid ${o.c}` : "1px solid #e5e7eb",
                    borderRadius: 6, fontWeight: 600, cursor: "pointer", fontSize: 12,
                  }}>{o.l}</button>
                ))}
              </div>
            </div>
          )}

          {/* Sign mode toggle */}
          <div style={{ display: "flex", gap: 4, marginBottom: 12, background: "#f3f4f6", borderRadius: 8, padding: 3 }}>
            <button onClick={() => setSignMode("draw")} style={{ flex: 1, padding: 7, background: signMode === "draw" ? "#3b82f6" : "transparent", color: signMode === "draw" ? "white" : "#6b7280", border: "none", borderRadius: 6, fontWeight: 700, cursor: "pointer", fontSize: 12 }}>✏️ Draw</button>
            <button onClick={() => setSignMode("type")} style={{ flex: 1, padding: 7, background: signMode === "type" ? "#3b82f6" : "transparent", color: signMode === "type" ? "white" : "#6b7280", border: "none", borderRadius: 6, fontWeight: 700, cursor: "pointer", fontSize: 12 }}>⌨️ Type</button>
          </div>

          {signMode === "draw" ? (
            <MiniSignPad onSignature={setSignatureData} />
          ) : (
            <div style={{ border: "2px solid #d1d5db", borderRadius: 8, background: "#fafafa", overflow: "hidden", marginBottom: 8 }}>
              <canvas ref={typeCanvasRef} width={800} height={160} style={{ width: 400, height: 80, display: "block" }} />
            </div>
          )}

          <div style={{ marginTop: 12, marginBottom: 12 }}>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Notes (optional)" style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5e7eb", resize: "vertical", boxSizing: "border-box", fontSize: 13 }} />
          </div>

          <div style={{ padding: 10, background: "#fefce8", borderRadius: 8, fontSize: 11, color: "#854d0e", marginBottom: 16 }}>
            By signing, I confirm this {step.action === "PICKUP_SIGN" ? "pickup acceptance" : step.action === "DELIVERY_SIGN" ? "delivery receipt" : step.action === "RELEASE_ACKNOWLEDGE" ? "cargo release" : "delivery completion"}. This electronic signature is legally binding and blockchain-recorded.
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <button onClick={onClose} style={{ flex: 1, padding: 12, background: "#f3f4f6", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
            <button onClick={execute} disabled={executing} style={{ flex: 1, padding: 12, background: executing ? "#9ca3af" : "#10b981", color: "white", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>
              {executing ? "Signing..." : `✍️ ${step.label}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================
export default function LoadWorkflow() {
  const { adminEmail } = useAuth();
  const [loads, setLoads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [showStepModal, setShowStepModal] = useState(false);
  const [activeStep, setActiveStep] = useState(null);

  const headers = { "Content-Type": "application/json", "x-admin-email": adminEmail, "x-user-role": "BROKER_ADMIN" };

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    setLoading(true);
    try {
      const [lRes, sRes] = await Promise.all([
        fetch("/api/workflow/loads", { headers }).then(r => r.json()),
        fetch("/api/workflow/stats", { headers }).then(r => r.json()),
      ]);
      setLoads(lRes.loads || []);
      setStats(sRes);
    } catch (e) { console.error("Load error:", e); }
    finally { setLoading(false); }
  }

  async function loadWorkflow(loadId) {
    try {
      const res = await fetch(`/api/workflow/loads/${loadId}/status`, { headers });
      const data = await res.json();
      setWorkflow(data);
      setSelectedLoad(loadId);
    } catch (e) { console.error("Workflow error:", e); }
  }

  function openStep(step) {
    setActiveStep({ ...step, loadId: selectedLoad });
    setShowStepModal(true);
  }

  if (loading) return <div style={{ padding: 40, textAlign: "center" }}>Loading workflow...</div>;

  return (
    <div style={{ maxWidth: 1400, margin: "0 auto" }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 900, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 28 }}>🔄</span> Load Workflow
        </h1>
        <p style={{ margin: "4px 0 0 0", color: "#6b7280", fontSize: 14 }}>
          Freight lifecycle — pickup, transit, delivery, and payment release
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 16, marginBottom: 24 }}>
          {[
            { label: "Total Loads", value: stats.totalLoads, bg: "linear-gradient(135deg, #1e3a8a, #3b82f6)" },
            { label: "Awaiting Pickup", value: stats.awaitingPickup, bg: "linear-gradient(135deg, #92400e, #f59e0b)" },
            { label: "In Transit", value: stats.inTransit, bg: "linear-gradient(135deg, #3730a3, #6366f1)" },
            { label: "Delivered", value: stats.delivered, bg: "linear-gradient(135deg, #9d174d, #ec4899)" },
            { label: "Closed", value: stats.closed, bg: "linear-gradient(135deg, #065f46, #10b981)" },
          ].map((s, i) => (
            <div key={i} style={{ background: s.bg, borderRadius: 12, padding: 20, color: "white" }}>
              <div style={{ fontSize: 32, fontWeight: 900 }}>{s.value}</div>
              <div style={{ opacity: 0.8, fontSize: 13 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "400px 1fr", gap: 24 }}>
        {/* Load List */}
        <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ padding: "16px 20px", borderBottom: "1px solid #e5e7eb", fontWeight: 700 }}>📋 Loads</div>
          <div style={{ maxHeight: 600, overflowY: "auto" }}>
            {loads.map(l => {
              const sc = STATUS_COLORS[l.status] || { bg: "#f3f4f6", color: "#6b7280" };
              return (
                <div key={l.id} onClick={() => loadWorkflow(l.id)} style={{
                  padding: "12px 16px", borderBottom: "1px solid #f3f4f6", cursor: "pointer",
                  background: selectedLoad === l.id ? "#f0f4ff" : "white",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{l.loadNumber}</div>
                    <span style={{ padding: "2px 8px", background: sc.bg, color: sc.color, borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{l.status}</span>
                  </div>
                  <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{l.origin} → {l.destination}</div>
                  {/* Progress bar */}
                  <div style={{ marginTop: 6, height: 4, background: "#e5e7eb", borderRadius: 2 }}>
                    <div style={{ height: 4, background: l.progress >= 100 ? "#10b981" : "#3b82f6", borderRadius: 2, width: `${l.progress}%`, transition: "width 0.3s" }} />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 11, color: "#9ca3af" }}>
                    <span>{l.carrier || "No carrier"}</span>
                    <span>{l.docCount} docs, {l.sigCount} sigs</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Workflow Timeline */}
        <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 12, padding: 24 }}>
          {workflow ? (
            <>
              {/* Load header */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900 }}>Load {workflow.loadNumber}</h2>
                    <div style={{ fontSize: 14, color: "#6b7280", marginTop: 4 }}>{workflow.origin} → {workflow.destination}</div>
                    {workflow.commodity && <div style={{ fontSize: 13, color: "#9ca3af" }}>{workflow.commodity}</div>}
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <span style={{ padding: "4px 12px", background: (STATUS_COLORS[workflow.currentStatus] || {}).bg || "#f3f4f6", color: (STATUS_COLORS[workflow.currentStatus] || {}).color || "#6b7280", borderRadius: 20, fontSize: 13, fontWeight: 700 }}>
                      {workflow.currentStatus}
                    </span>
                    <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>
                      {workflow.customer && <>Customer: {workflow.customer}</>}
                      {workflow.carrier && <> | Carrier: {workflow.carrier}</>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div style={{ position: "relative" }}>
                {workflow.steps.map((step, i) => {
                  const sc = STEP_COLORS[step.status];
                  const isLast = i === workflow.steps.length - 1;

                  return (
                    <div key={step.step} style={{ display: "flex", gap: 16, marginBottom: isLast ? 0 : 0, position: "relative" }}>
                      {/* Timeline line + dot */}
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 40 }}>
                        <div style={{ width: 36, height: 36, borderRadius: "50%", background: sc.bg, border: `3px solid ${sc.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, zIndex: 1 }}>
                          {sc.icon}
                        </div>
                        {!isLast && <div style={{ width: 3, flex: 1, background: step.status === "COMPLETED" ? "#10b981" : "#e5e7eb", minHeight: 40 }} />}
                      </div>

                      {/* Step content */}
                      <div style={{ flex: 1, paddingBottom: isLast ? 0 : 24 }}>
                        <div style={{ background: sc.bg, border: `1px solid ${sc.border}`, borderRadius: 12, padding: 16 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                            <div>
                              <div style={{ fontWeight: 900, fontSize: 15, color: sc.text }}>
                                Step {step.step}: {step.label}
                              </div>
                              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{step.description}</div>
                              <div style={{ fontSize: 12, marginTop: 4 }}>
                                <span style={{ color: "#9ca3af" }}>Required signer:</span>{" "}
                                <strong>{step.signerRole}</strong>
                                {" | "}
                                <span style={{ color: "#9ca3af" }}>Document:</span>{" "}
                                <strong>{step.documentType}</strong>
                                {step.hasDocument ? " ✓" : " (will be auto-generated)"}
                              </div>
                            </div>
                            {step.status === "CURRENT" && (
                              <button onClick={() => openStep(step)} style={{ padding: "8px 16px", background: "#3b82f6", color: "white", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: 13, whiteSpace: "nowrap" }}>
                                ✍️ Sign Now
                              </button>
                            )}
                          </div>

                          {/* Signature info if completed */}
                          {step.signature && (
                            <div style={{ marginTop: 12, padding: 10, background: "rgba(255,255,255,0.7)", borderRadius: 8, fontSize: 12 }}>
                              <div style={{ fontWeight: 700, color: "#059669" }}>
                                ✅ Signed by {step.signature.signer} ({step.signature.signerRole})
                              </div>
                              <div style={{ color: "#6b7280" }}>
                                {step.signature.timestamp ? new Date(step.signature.timestamp).toLocaleString() : ""}
                                {" | Type: "}{step.signature.signatureType}
                              </div>
                            </div>
                          )}

                          {/* Status after */}
                          {step.status === "COMPLETED" && (
                            <div style={{ marginTop: 8, fontSize: 11, color: "#059669" }}>
                              Load status → <strong>{step.statusAfter}</strong>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Completion banner */}
              {workflow.isComplete && (
                <div style={{ marginTop: 24, padding: 20, background: "#d1fae5", borderRadius: 12, textAlign: "center" }}>
                  <div style={{ fontSize: 32 }}>🎉</div>
                  <div style={{ fontWeight: 900, fontSize: 18, color: "#065f46" }}>Workflow Complete!</div>
                  <div style={{ fontSize: 13, color: "#047857" }}>All signatures collected. Load is closed and payment authorized.</div>
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: "center", color: "#9ca3af", padding: 60 }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🔄</div>
              <div>Select a load from the list to view its workflow</div>
            </div>
          )}
        </div>
      </div>

      {/* Step Modal */}
      {showStepModal && activeStep && (
        <StepModal
          step={activeStep}
          loadNumber={workflow?.loadNumber}
          headers={headers}
          onClose={() => { setShowStepModal(false); setActiveStep(null); }}
          onExecuted={() => { loadAll(); if (selectedLoad) loadWorkflow(selectedLoad); }}
        />
      )}
    </div>
  );
}
